import React, { useContext, useState } from "react";
import { useMutation } from "react-apollo";
import PropTypes from "prop-types";

// Import Polaris Components
import { Stack, Modal } from "@shopify/polaris";

// import context
import { PrivateContext } from "lib/context";

// import helper
import { baseHelper, errorHelper } from "lib/helpers";

// import constant
import constant from "lib/constant/constant";

// import gql
import {
  SYNC_INVENTORY_VENDOR,
  DISCONNECT_SHOP,
  SYNC_WOOCOMMERCE_VENDOR_PRODUCT,
} from "app/productOld/apollo/mutations/connect";
import { DISCONNECT_WOOCOMMERCE_SHOP } from "app/productOld/apollo/mutations/wooCommerceConnect";
import {
  DISCONNECT_MAGENTO_SHOP,
  DISCONNECT_WIX_SHOP,
  DISCONNECT_PRESTASHOP,
  SYNC_VENDOR_MAGENTO_PRODUCT,
  SYNC_VENDOR_WIX_PRODUCT,
  SYNC_VENDOR_PRESTASHOP_PRODUCT,
} from "app/product/apollo/mutations";
import { SYNC_VENDOR_DMS_PRODUCT, DISCONNECT_DMS_SHOP } from "app/productOld/apollo/mutations";

// import report
import { QuickAction, QuickReport } from "app/reports";
import { AddProductMajor, ImportStoreMajor, OnlineStoreMajor, StoreMajor } from "@shopify/polaris-icons";

const {
  gql: {
    SYNC_INVENTORY_VENDOR: SYNC_INVENTORY_VENDOR_GQL,
    DISCONNECT_SHOP: DISCONNECT,
    DISCONNECT_WOOCOMMERCE_SHOP: DISCONNECT_WOO_SHOP,
    DISCONNECT_WIX_SHOP: DISCONNECT_WIX,
    DISCONNECT_MAGENTO_SHOP: DISCONNECT_MAGENTO,
    DISCONNECT_PRESTASHOP: DISCONNECT_PRESTA_SHOP, 
    SYNC_WIX_VENDOR_PRODUCT,
    SYNC_WOOCOMMERCE_VENDOR_PRODUCT: SYNC_WOOCOMMERCE_VENDOR_PRODUCT_GQL,
    SYNC_VENDOR_MAGENTO_PRODUCT: SYNC_MAGENTO_VENDOR_PRODUCT_GQL,
    SYNC_VENDOR_PRESTASHOP_PRODUCT: SYNC_PRESTASHOP_VENDOR_PRODUCT_GQL,
  },
} = constant;

const Widget = (props) => {
  const { setBanner = () => {} } = props;
  const { cms, currentUser = {}, history = {}, currentUserRefetch = () => {} } = useContext(PrivateContext);

  const { shop = null } = currentUser;

  // Vendors under this seller cannot disconnect their own shop — it has to go
  // through seller support. Matches on introductorySeller (a vendor) as well as
  // _id (the seller themselves), the same idiom used for isOblist/isMaison.
  const isDisconnectRestricted =
    !!currentUser && [currentUser.introductorySeller, currentUser._id].includes("68113653effa140019f2e719");
  const disconnectRestrictedLabel =
    "Store disconnection requests must be processed by <b>XHAWI Seller Support.</b> <br />" +
    ' Please contact us at <a href="mailto:seller_support@xhawi.com">seller_support@xhawi.com</a>' +
    " and our team will assist you with your request.";

  const {
    magento: magentoConfig = {},
    wix: wixConfig = {},
    wooCommerce: wooCommerceConfig = {},
    prestaShop: prestaShopConfig = {},
    dms: dmsConfig = {},
  } = (currentUser && currentUser.storeConfig) || {};
  // DMS has no store domain/token to key off - the connect form only persists
  // channelCode/vendorCode, so isActive is the whole connection state.
  const { isActive: isDmsActive = false } = dmsConfig || {};
  const { shop: woocommerceVendorShop = "" } = wooCommerceConfig || {};
  const { shop: magentoVendorShop = "" } = magentoConfig || {};
  const { shop: prestaShopVendorShop = "" } = prestaShopConfig || {};
  const { instanceId: wixInstanceId = "", accessKey: wixAccessKey = "" } = wixConfig || {};

  const [syncInventoryVendor, { loading: syncInventoryVendorLoading }] = useMutation(SYNC_INVENTORY_VENDOR);
  const [syncWooCommerceVendorProduct, { loading: syncWooCommerceVendorProductLoading }] = useMutation(
    SYNC_WOOCOMMERCE_VENDOR_PRODUCT
  );
  const [syncVendorWixProduct, { loading: syncVendorWixProductLoading }] = useMutation(SYNC_VENDOR_WIX_PRODUCT);
  const [syncVendorMagentoProduct, { loading: syncVendorMagentoProductLoading }] = useMutation(SYNC_VENDOR_MAGENTO_PRODUCT);
  const [syncVendorPrestaShopProduct, { loading: syncVendorPrestaShopProductLoading }] = useMutation(SYNC_VENDOR_PRESTASHOP_PRODUCT);
  const [disconnectShop, { loading: disconnectLoading }] = useMutation(DISCONNECT_SHOP);
  const [disconnectWooCommerceShop, { loading: disconnectWooCommerceLoading }] = useMutation(
    DISCONNECT_WOOCOMMERCE_SHOP
  );
  const [disconnectWixShop, { loading: disconnectWixLoading }] = useMutation(DISCONNECT_WIX_SHOP);
  const [disconnectMagentoShop, { loading: disconnectMagentoLoading }] = useMutation(DISCONNECT_MAGENTO_SHOP);
  const [disconnectPrestaShop, { loading: disconnectPrestaShopLoading }] = useMutation(DISCONNECT_PRESTASHOP);
  const [syncVendorDmsProduct, { loading: syncVendorDmsProductLoading }] = useMutation(SYNC_VENDOR_DMS_PRODUCT);
  const [disconnectDmsShop, { loading: disconnectDmsLoading }] = useMutation(DISCONNECT_DMS_SHOP);

  const [isDisconnectActive, setIsDisconnectActive] = useState(false);
  const [isWooCommerceDisconnectActive, setIsWooCommerceDisconnectActive] = useState(false);
  const [isMagentoDisconnectActive, setIsMagentoDisconnectActive] = useState(false);
  const [isPrestaShopDisconnectActive, setIsPrestaShopDisconnectActive] = useState(false);
  const [isWixDisconnectActive, setWixDisconnectActive] = useState(false);
  const [isDmsDisconnectActive, setDmsDisconnectActive] = useState(false);

  if (
    !(
      shop ||
      woocommerceVendorShop ||
      magentoVendorShop ||
      (wixInstanceId && wixAccessKey) ||
      prestaShopVendorShop ||
      isDmsActive
    )
  ) {
    return (
      <>
        <QuickReport />
        <QuickAction />
      </>
    );
  }

  const syncProducts = async () => {
    try {
      const response = await syncInventoryVendor();
      const responseError = baseHelper.getResponseError(response.data, SYNC_INVENTORY_VENDOR_GQL);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      const onAction = () => history.push("/products/shopify");
      const bannerAction = {
        onAction,
        content: cms("section.sync.label.view"),
      };
      setBanner({
        isOpen: true,
        status: constant.INFO,
        title: cms("message.success.sync"),
        action: bannerAction,
      });
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const syncWooCommerceProducts = async () => {
    try {
      const response = await syncWooCommerceVendorProduct();
      const responseError = baseHelper.getResponseError(response.data, SYNC_WOOCOMMERCE_VENDOR_PRODUCT_GQL);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      const onAction = () => history.push("/products/woocommerce");
      const bannerAction = {
        onAction,
        content: `${cms("section.connect.button.viewWoocommerceProduct")}`,
      };
      setBanner({
        isOpen: true,
        status: constant.INFO,
        title: cms("message.success.sync"),
        action: bannerAction,
      });
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const syncWixProducts = async () => {
    try {
      const response = await syncVendorWixProduct();
      const responseError = baseHelper.getResponseError(response.data, SYNC_WIX_VENDOR_PRODUCT);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      const onAction = () => history.push("/products/wix");
      const bannerAction = {
        onAction,
        content: `${cms("section.connect.button.viewWixProduct")}`,
      };
      setBanner({
        isOpen: true,
        status: "info",
        title: cms("message.success.sync"),
        action: bannerAction,
      });
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const syncMagentoProducts = async () => {
    try {
      const response = await syncVendorMagentoProduct();
      const responseError = baseHelper.getResponseError(response.data, SYNC_MAGENTO_VENDOR_PRODUCT_GQL);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      const onAction = () => history.push("/products/magento");
      const bannerAction = {
        onAction,
        content: `${cms("section.connect.button.viewWixProduct")}`,
      };
      setBanner({
        isOpen: true,
        status: "info",
        title: cms("message.success.sync"),
        action: bannerAction,
      });
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const syncPrestaShopProducts = async () => {
    try {
      const response = await syncVendorPrestaShopProduct();
      const responseError = baseHelper.getResponseError(response.data, SYNC_PRESTASHOP_VENDOR_PRODUCT_GQL);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      const onAction = () => history.push("/products/prestashop");
      const bannerAction = {
        onAction,
        content: `${cms("section.connect.button.viewWixProduct")}`,
      };
      setBanner({
        isOpen: true,
        status: "info",
        title: cms("message.success.sync"),
        action: bannerAction,
      });
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const disconnectShopConfirm = async () => {
    try {
      const response = await disconnectShop({
        variables: { input: { id: currentUser.id } },
      });
      setIsDisconnectActive(!isDisconnectActive);
      const responseError = baseHelper.getResponseError(response.data, DISCONNECT);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      setBanner({ isOpen: true, status: constant.SUCCESS, title: cms("message.success.disconnect") });
      setTimeout(() => {
        currentUserRefetch();
      }, 1000);
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const disconnectWooCommerceShopConfirm = async () => {
    try {
      const response = await disconnectWooCommerceShop();
      setIsWooCommerceDisconnectActive(!isWooCommerceDisconnectActive);
      const responseError = baseHelper.getResponseError(response.data, DISCONNECT_WOO_SHOP);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      setBanner({
        isOpen: true,
        status: constant.SUCCESS,
        title: `${cms("section.connect.message.success.disconnected")}`,
      });
      setTimeout(() => {
        currentUserRefetch();
      }, 1000);
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  // DMS has no OAuth/webhook channel, so "import" means pulling the vendor's
  // DMSMatrix catalog on demand instead of waiting for the 6h background cron.
  const syncDmsProducts = async () => {
    try {
      const response = await syncVendorDmsProduct();
      const responseError = baseHelper.getResponseError(response.data, "syncVendorDmsProduct");
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      const bannerAction = {
        onAction: () => history.push("/products/dms"),
        content: cms("section.connect.button.viewDmsProduct"),
      };
      setBanner({
        isOpen: true,
        status: "info",
        title: cms("message.success.sync"),
        action: bannerAction,
      });
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const disconnectDmsShopConfirm = async () => {
    try {
      const response = await disconnectDmsShop();
      const responseError = baseHelper.getResponseError(response.data, "disconnectDmsShop");
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      const responseData = baseHelper.getResponseData(response.data, "disconnectDmsShop");
      if (responseData) {
        setDmsDisconnectActive(!isDmsDisconnectActive);
        setBanner({
          isOpen: true,
          status: constant.SUCCESS,
          title: `${cms("section.connect.message.success.disconnected")}`,
        });
        setTimeout(() => {
          currentUserRefetch();
        }, 1000);
      }
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const disconnectWixShopConfirm = async () => {
    try {
      const response = await disconnectWixShop();
      const responseError = baseHelper.getResponseError(response.data, DISCONNECT_WIX);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      const responseData = baseHelper.getResponseData(response.data, DISCONNECT_WIX);
      if (responseData) {
        setWixDisconnectActive(!isWixDisconnectActive);
        setBanner({
          isOpen: true,
          status: constant.SUCCESS,
          title: `${cms("section.connect.message.success.disconnected")}`,
        });
        setTimeout(() => {
          currentUserRefetch();
        }, 1000);
      }
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const disconnectMagentoShopConfirm = async () => {
    try {
      const response = await disconnectMagentoShop();
      setIsMagentoDisconnectActive(!isMagentoDisconnectActive);
      const responseError = baseHelper.getResponseError(response.data, DISCONNECT_MAGENTO);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      setBanner({
        isOpen: true,
        status: constant.SUCCESS,
        title: `${cms("section.connect.message.success.disconnected")}`,
      });
      setTimeout(() => {
        currentUserRefetch();
      }, 1000);
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const disconnectPrestaShopConfirm = async () => {
    try {
      const response = await disconnectPrestaShop();
      setIsPrestaShopDisconnectActive(!isPrestaShopDisconnectActive);
      const responseError = baseHelper.getResponseError(response.data, DISCONNECT_PRESTA_SHOP);
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }
      setBanner({
        isOpen: true,
        status: constant.SUCCESS,
        title: `${cms("section.connect.message.success.disconnected")}`,
      });
      setTimeout(() => {
        currentUserRefetch();
      }, 1000);
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const linkData = [
    {
      action: () => history.push("/products/add"),
      caption: cms("section.caption.add"),
      id: "addProduct",
      icon: AddProductMajor,
      label: cms("section.connect.label.add"),
      primary: true,
      shop: true,
    },
    {
      action: () => history.push("/products/edit"),
      caption: cms("section.caption.edit"),
      id: "editProducts",
      primary: true,
      label: cms("label.editProduct"),
    },
  ];
  const storedata = [];
  if (shop) {
    storedata.unshift(
      {
        action: () => syncProducts(),
        caption: cms("section.sync.label.import"),
        id: "importProduct",
        label: cms("section.connect.label.import"),
        icon: ImportStoreMajor,
        loading: syncInventoryVendorLoading,
        shop,
      },
      {
        action: () => history.push("/products/shopify"),
        caption: cms("section.connect.label.view"),
        id: "storeProduct",
        label: cms("section.connect.label.view"),
        icon: OnlineStoreMajor,
        shop,
      },
      {
        action: () => setIsDisconnectActive(!isDisconnectActive),
        caption: `${cms("section.disconnect.label.disconnectShop", { shop })}`,
        id: "disconnectShop",
        label: `${cms("section.disconnect.label.disconnect", { shop })}`,
        icon: StoreMajor,
        shop,
      }
    );
  }

  if (woocommerceVendorShop) {
    storedata.unshift(
      {
        action: () => syncWooCommerceProducts(),
        caption: cms("section.connect.label.importWooCommerce"),
        id: "importProduct",
        icon: ImportStoreMajor,
        label: cms("section.connect.label.importWooCommerce"),
        loading: syncWooCommerceVendorProductLoading,
        shop: woocommerceVendorShop,
      },
      {
        action: () => history.push("/products/woocommerce"),
        caption: cms("section.connect.label.view"),
        id: "storeProduct",
        icon: OnlineStoreMajor,
        label: cms("section.connect.label.wooCommerce"),
        shop: woocommerceVendorShop,
      },
      {
        action: () => setIsWooCommerceDisconnectActive(!isWooCommerceDisconnectActive),
        caption: `${cms("section.disconnect.label.wooCommerce", { woocommerceVendorShop })}`,
        id: "disconnectWooCommerceShop",
        icon: StoreMajor,
        label: `${cms("section.disconnect.label.wooCommerceShop", { woocommerceVendorShop })}`,
        shop: woocommerceVendorShop,
      }
    );
  }

  if (isDmsActive) {
    storedata.unshift(
      {
        action: () => syncDmsProducts(),
        caption: cms("section.connect.label.importDms"),
        id: "importProduct",
        icon: ImportStoreMajor,
        label: cms("section.connect.label.importDms"),
        loading: syncVendorDmsProductLoading,
        shop: true,
      },
      {
        action: () => history.push("/products/dms"),
        caption: cms("section.connect.label.viewDms"),
        id: "storeProduct",
        icon: OnlineStoreMajor,
        label: cms("section.connect.label.dms"),
        shop: true,
      },
      {
        action: () => setDmsDisconnectActive(!isDmsDisconnectActive),
        caption: cms("section.disconnect.label.dms"),
        id: "disconnectDmsShop",
        icon: StoreMajor,
        label: cms("section.disconnect.label.dmsShop"),
        shop: true,
      }
    );
  }

  if (wixInstanceId && wixAccessKey) {
    storedata.unshift(
      {
        action: () => syncWixProducts(),
        caption: cms("section.connect.label.importWix"),
        id: "importProduct",
        icon: ImportStoreMajor,
        label: cms("section.connect.label.importWix"),
        loading: syncVendorWixProductLoading,
        shop: true,
      },
      {
        action: () => history.push("/products/wix"),
        caption: cms("section.connect.label.viewWix"),
        id: "storeProduct",
        icon: OnlineStoreMajor,
        label: cms("section.connect.label.wix"),
        shop: true,
      },
      {
        action: () => setWixDisconnectActive(!isWixDisconnectActive),
        caption: cms("section.disconnect.label.wix"),
        id: "disconnectWixShop",
        icon: StoreMajor,
        label: cms("section.disconnect.label.wixShop"),
        shop: true,
      }
    );
  }

  if (magentoVendorShop) {
    storedata.unshift(
      {
        action: () => syncMagentoProducts(),
        caption: "Import Magento products",
        id: "importProduct",
        icon: ImportStoreMajor,
        label: "Import Magento products",
        loading: syncVendorMagentoProductLoading,
        shop: true,
      },
      {
        action: () => history.push("/products/magento"),
        caption: "View products from your Magento store",
        id: "storeProduct",
        icon: OnlineStoreMajor,
        label: "View products from your Magento store",
        shop: true,
      },
      {
        action: () => setIsMagentoDisconnectActive(!isMagentoDisconnectActive),
        caption: `${cms("section.disconnect.label.wooCommerce", { woocommerceVendorShop: magentoVendorShop })}`,
        id: "disconnectWooCommerceShop",
        icon: StoreMajor,
        label: `${cms("section.disconnect.label.wooCommerceShop", { woocommerceVendorShop: magentoVendorShop })}`,
        shop: magentoVendorShop,
      }
    );
  }

  if (prestaShopVendorShop) {
    storedata.unshift(
      {
        action: () => syncPrestaShopProducts(),
        caption: "Import Presta Shop products",
        id: "importProduct",
        icon: ImportStoreMajor,
        label: "Import Presta Shop products",
        loading: syncVendorPrestaShopProductLoading,
        shop: true,
      },
      {
        action: () => history.push("/products/prestashop"),
        caption: "View products from your Presta Shop store",
        id: "storeProduct",
        icon: OnlineStoreMajor,
        label: "View products from your Presta Shop store",
        shop: true,
      },
      {
        action: () => setIsPrestaShopDisconnectActive(!isPrestaShopDisconnectActive),
        caption: `${cms("section.disconnect.label.wooCommerce", { woocommerceVendorShop: prestaShopVendorShop })}`,
        id: "disconnectWooCommerceShop",
        icon: StoreMajor,
        label: `${cms("section.disconnect.label.wooCommerceShop", { woocommerceVendorShop: prestaShopVendorShop })}`,
        shop: magentoVendorShop,
      }
    );
  }

  return (
    <>
      <Stack.Item>
        <Modal
          open={isDisconnectActive}
          onClose={() => setIsDisconnectActive(!isDisconnectActive)}
          title={cms("section.sync.label.disconnect")}
          primaryAction={{
            content: `${cms("section.sync.label.disconnect")} ${currentUser.shop}`,
            onAction: disconnectShopConfirm,
            destructive: true,
            loading: disconnectLoading,
            disabled: isDisconnectRestricted,
          }}
          secondaryActions={[
            {
              content: cms("button.cancel"),
              onAction: () => setIsDisconnectActive(!isDisconnectActive),
            },
          ]}
        >
          <Modal.Section>
            {isDisconnectRestricted ? (
              // eslint-disable-next-line react/no-danger
              <div dangerouslySetInnerHTML={{ __html: disconnectRestrictedLabel }} />
            ) : (
              `${cms("section.sync.label.disconnectShop")}`
            )}
          </Modal.Section>
        </Modal>
      </Stack.Item>

      <Stack.Item>
        <Modal
          open={isWooCommerceDisconnectActive}
          onClose={() => setIsWooCommerceDisconnectActive(!isWooCommerceDisconnectActive)}
          title={cms("section.sync.label.disconnect")}
          primaryAction={{
            content: `${cms("section.connect.label.disconnect")}`,
            onAction: disconnectWooCommerceShopConfirm,
            destructive: true,
            loading: disconnectWooCommerceLoading,
            disabled: isDisconnectRestricted,
          }}
          secondaryActions={[
            {
              content: `${cms("button.cancel")}`,
              onAction: () => setIsWooCommerceDisconnectActive(!isWooCommerceDisconnectActive),
            },
          ]}
        >
          <Modal.Section>
            {isDisconnectRestricted ? (
              // eslint-disable-next-line react/no-danger
              <div dangerouslySetInnerHTML={{ __html: disconnectRestrictedLabel }} />
            ) : (
              `${cms("section.connect.label.disconnectShop")} ${woocommerceVendorShop}?`
            )}
          </Modal.Section>
        </Modal>
      </Stack.Item>

      <Stack.Item>
        <Modal
          open={isMagentoDisconnectActive}
          onClose={() => setIsMagentoDisconnectActive(!isMagentoDisconnectActive)}
          title={cms("section.sync.label.disconnect")}
          primaryAction={{
            content: `${cms("section.connect.label.disconnect")}`,
            onAction: disconnectMagentoShopConfirm,
            destructive: true,
            loading: disconnectMagentoLoading,
          }}
          secondaryActions={[
            {
              content: `${cms("button.cancel")}`,
              onAction: () => setIsMagentoDisconnectActive(!isMagentoDisconnectActive),
            },
          ]}
        >
          <Modal.Section>{`${cms("section.connect.label.disconnectShop")} ${magentoVendorShop}?`}</Modal.Section>
        </Modal>
      </Stack.Item>

      <Stack.Item>
        <Modal
          open={isPrestaShopDisconnectActive}
          onClose={() => setIsPrestaShopDisconnectActive(!isPrestaShopDisconnectActive)}
          title={cms("section.sync.label.disconnect")}
          primaryAction={{
            content: `${cms("section.connect.label.disconnect")}`,
            onAction: disconnectPrestaShopConfirm,
            destructive: true,
            loading: disconnectPrestaShopLoading,
          }}
          secondaryActions={[
            {
              content: `${cms("button.cancel")}`,
              onAction: () => setIsPrestaShopDisconnectActive(!isPrestaShopDisconnectActive),
            },
          ]}
        >
          <Modal.Section>{`${cms("section.connect.label.disconnectShop")} ${magentoVendorShop}?`}</Modal.Section>
        </Modal>
      </Stack.Item>

      <Stack.Item>
        <Modal
          open={isWixDisconnectActive}
          onClose={() => setWixDisconnectActive(!isWixDisconnectActive)}
          title={cms("section.sync.label.disconnect")}
          primaryAction={{
            content: `${cms("section.connect.label.disconnect")}`,
            onAction: disconnectWixShopConfirm,
            destructive: true,
            loading: disconnectWixLoading,
          }}
          secondaryActions={[
            {
              content: `${cms("button.cancel")}`,
              onAction: () => setWixDisconnectActive(!isWixDisconnectActive),
            },
          ]}
        >
          <Modal.Section>
            {`${cms("section.connect.label.disconnectShop")} ${cms("section.connect.label.wixShop")}?`}
          </Modal.Section>
        </Modal>
      </Stack.Item>

      <Stack.Item>
        <Modal
          open={isDmsDisconnectActive}
          onClose={() => setDmsDisconnectActive(!isDmsDisconnectActive)}
          title={cms("section.sync.label.disconnect")}
          primaryAction={{
            content: `${cms("section.connect.label.disconnect")}`,
            onAction: disconnectDmsShopConfirm,
            destructive: true,
            loading: disconnectDmsLoading,
            // DMS is only enabled for the seller whose vendors must route
            // disconnection through seller support, so this is always gated today.
            disabled: isDisconnectRestricted,
          }}
          secondaryActions={[
            {
              content: `${cms("button.cancel")}`,
              onAction: () => setDmsDisconnectActive(!isDmsDisconnectActive),
            },
          ]}
        >
          <Modal.Section>
            {isDisconnectRestricted ? (
              // eslint-disable-next-line react/no-danger
              <div dangerouslySetInnerHTML={{ __html: disconnectRestrictedLabel }} />
            ) : (
              `${cms("section.connect.label.disconnectShop")} ${cms("section.connect.label.dmsShop")}?`
            )}
          </Modal.Section>
        </Modal>
      </Stack.Item>

      <QuickReport />
      <QuickAction link={linkData} storedata={storedata} />
    </>
  );
};

Widget.propTypes = {
  setBanner: PropTypes.func.isRequired,
};

export default Widget;
